import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { uploadFile } from '../../firebase/r2storage';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, Loader, FileText, Linkedin, Copy, CheckCircle, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

export default function AddCandidate() {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [interview, setInterview] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [lastAddedCandidate, setLastAddedCandidate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    linkedinProfile: '',
    phone: ''
  });

  useEffect(() => {
    fetchInterview();
  }, [interviewId]);

  const fetchInterview = async () => {
    try {
      const docRef = doc(db, 'interviews', interviewId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setInterview({ id: docSnap.id, ...docSnap.data() });
      }
    } catch (error) {
      console.error('Error fetching interview:', error);
      toast.error('Failed to load interview');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setResumeFile(file);
    } else {
      toast.error('Please upload a PDF file');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resumeFile) {
      toast.error('Please upload a resume');
      return;
    }

    setLoading(true);

    try {
      // Upload resume to R2 Storage
      const resumePath = `resumes/${interviewId}/${Date.now()}_${resumeFile.name}`;
      const uploadResult = await uploadFile(resumeFile, resumePath);
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Resume upload failed');
      }
      
      const resumeUrl = uploadResult.url;

      // Create candidate object
      const candidateId = `candidate_${Date.now()}`;
      const candidate = {
        id: candidateId,
        ...formData,
        resumeUrl,
        resumeFileName: resumeFile.name,
        addedAt: new Date().toISOString(),
        status: 'pending',
        interviewLink: `${window.location.origin}/interview/${interviewId}/${candidateId}`
      };

      // Update interview document with new candidate
      const interviewRef = doc(db, 'interviews', interviewId);
      await updateDoc(interviewRef, {
        candidates: arrayUnion(candidate)
      });

      toast.success('Candidate added successfully!');
      setLastAddedCandidate(candidate);
      
      // Reset form
      setFormData({ name: '', email: '', linkedinProfile: '', phone: '' });
      setResumeFile(null);
      
    } catch (error) {
      console.error('Error adding candidate:', error);
      toast.error('Failed to add candidate');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  if (!interview) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/admin/interview-management')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Interviews
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {interview.positionTitle}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {interview.department} • {interview.experienceLevel}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Add Candidate
        </h1>

        {lastAddedCandidate && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg">
            <div className="flex items-start gap-3 mb-3">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-1">
                  Candidate Added Successfully!
                </h3>
                <p className="text-sm text-green-800 dark:text-green-200 mb-3">
                  {lastAddedCandidate.name} has been added. Share the interview link below:
                </p>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-300 dark:border-green-700">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Interview Link:
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={lastAddedCandidate.interviewLink}
                      readOnly
                      className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-blue-600 dark:text-blue-400 font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(lastAddedCandidate.interviewLink)}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Copy
                    </button>
                    <a
                      href={lastAddedCandidate.interviewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open
                    </a>
                  </div>
                </div>

                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => setLastAddedCandidate(null)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
                  >
                    Add Another Candidate
                  </button>
                  <button
                    onClick={() => navigate('/admin/interview-management')}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-sm"
                  >
                    Back to Interviews
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="+1 234 567 8900"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                <Linkedin className="w-4 h-4" />
                LinkedIn Profile
              </label>
              <input
                type="url"
                name="linkedinProfile"
                value={formData.linkedinProfile}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="https://linkedin.com/in/johndoe"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Resume (PDF) *
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg hover:border-blue-500 transition-colors">
              <div className="space-y-1 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600 dark:text-gray-400">
                  <label className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none">
                    <span>Upload a file</span>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="sr-only"
                      required
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">PDF up to 10MB</p>
                {resumeFile && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                    ✓ {resumeFile.name}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
              AI Processing
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-400">
              Our AI will automatically parse the resume and LinkedIn profile to extract relevant information
              and match it against the job requirements. The candidate will receive an interview link via email.
            </p>
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/interview-management')}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Adding Candidate...
                </>
              ) : (
                'Add Candidate'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
